import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Copy, Check, Wrench } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

interface Props {
  id: string;
  role: 'user' | 'assistant' | 'tool_call';
  content: string;
  toolCalls?: { name: string; args: Record<string, any>; result?: Record<string, any> }[];
  feedback?: 'helpful' | 'not_helpful';
  onFeedback?: (id: string, feedback: 'helpful' | 'not_helpful') => void;
}

function SimpleMarkdown({ content }: { content: string }) {
  const lines = content.split('\n');
  return (
    <div className="space-y-1.5">
      {lines.map((line, idx) => {
        const processed = line
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 rounded bg-white/6 text-[#e0e0e8] text-[12px]">$1</code>');

        if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
          return <li key={idx} className="ml-4 text-sm text-[#e0e0e8]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processed.replace(/^[-*]\s/, '')) }} />;
        }
        if (/^\d+\.\s/.test(line.trim())) {
          return <li key={idx} className="ml-4 text-sm text-[#e0e0e8] list-decimal" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processed.replace(/^\d+\.\s/, '')) }} />;
        }
        if (line.trim().startsWith('### ')) {
          return <h4 key={idx} className="text-sm font-semibold text-white mt-2">{line.replace(/^###\s/, '')}</h4>;
        }
        if (line.trim().startsWith('## ')) {
          return <h3 key={idx} className="text-sm font-bold text-white mt-2">{line.replace(/^##\s/, '')}</h3>;
        }
        if (!line.trim()) return <div key={idx} className="h-1" />;
        return <p key={idx} className="text-sm text-[#e0e0e8]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processed) }} />;
      })}
    </div>
  );
}

export default function MessageBubble({ id, role, content, toolCalls, feedback, onFeedback }: Props) {
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (role === 'tool_call' && toolCalls) {
    return (
      <div className="ltr:mr-8 rtl:ml-8 space-y-1.5">
        {toolCalls.map((tc, i) => (
          <div key={i} className="rounded-lg p-2.5 bg-amber-500/6 border border-amber-500/20">
            <div className="flex items-center gap-1.5 mb-1">
              <Wrench size={12} className="text-amber-400" />
              <span className="text-xs font-medium text-amber-400">{tc.name.replace(/_/g, ' ')}</span>
            </div>
            {tc.result && (
              <p className="text-xs text-[#a0a0b0] truncate">
                {tc.result.message || tc.result.error || JSON.stringify(tc.result).slice(0, 100)}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`${
      role === 'user'
        ? 'ltr:ml-8 rtl:mr-8 bg-iris-600/20 border-iris-500/20'
        : 'ltr:mr-8 rtl:ml-8 bg-white/2 border-white/8'
    } rounded-lg p-3 border group`}>
      {role === 'assistant' ? (
        <>
          <SimpleMarkdown content={content} />
          <div className="flex items-center gap-1 mt-3 pt-2 border-t border-white/5">
            <button
              onClick={() => onFeedback?.(id, 'helpful')}
              className={`p-1.5 rounded transition-colors ${
                feedback === 'helpful'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-[#9a9aac] hover:text-emerald-400 hover:bg-emerald-500/10'
              }`}
            >
              <ThumbsUp size={14} />
            </button>
            <button
              onClick={() => onFeedback?.(id, 'not_helpful')}
              className={`p-1.5 rounded transition-colors ${
                feedback === 'not_helpful'
                  ? 'bg-red-500/20 text-red-400'
                  : 'text-[#9a9aac] hover:text-red-400 hover:bg-red-500/10'
              }`}
            >
              <ThumbsDown size={14} />
            </button>
            <div className="flex-1" />
            <button
              onClick={handleCopy}
              className="p-1.5 rounded text-[#9a9aac] hover:text-white hover:bg-white/8 transition-colors"
            >
              {copiedId === id ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
            </button>
          </div>
        </>
      ) : (
        <p className="text-sm text-white">{content}</p>
      )}
    </div>
  );
}
